// types/AIUsageTypes.ts
// TypeScript types for broad AI usage tracking across all app features

import { Timestamp } from 'firebase/firestore';

/**
 * Categories of AI features in the app
 */
export enum AIFeatureCategory {
    OMORE_HELPER = 'omore_helper',
    IMAGE_GENERATION = 'image_generation',
    QUIZ_GENERATION = 'quiz_generation',
    TEXT_TO_SPEECH = 'text_to_speech',
    STORY_SPARKS = 'story_sparks',
    CAPTION_CLASH = 'caption_clash',
    DAILY_CHALLENGE = 'daily_challenge',
    CONTENT_MODERATION = 'content_moderation',
    NARRATIVE = 'narrative',
    PRESENTATION = 'presentation',
    VISION = 'vision',
    POSTY_IMAGE_GEN = 'posty_image_gen'
}

export const AIFeatureCategoryInfo: Record<AIFeatureCategory, {
    displayName: string;
    icon: string;
    color: string;
}> = {
    [AIFeatureCategory.OMORE_HELPER]: { displayName: 'Ask Omore', icon: '🤖', color: '#3b82f6' },
    [AIFeatureCategory.IMAGE_GENERATION]: { displayName: 'Image Generation', icon: '🎨', color: '#10b981' },
    [AIFeatureCategory.QUIZ_GENERATION]: { displayName: 'Quiz Generation', icon: '❓', color: '#f59e0b' },
    [AIFeatureCategory.TEXT_TO_SPEECH]: { displayName: 'Text-to-Speech', icon: '🎙️', color: '#8b5cf6' },
    [AIFeatureCategory.STORY_SPARKS]: { displayName: 'Story Sparks', icon: '✨', color: '#ec4899' },
    [AIFeatureCategory.CAPTION_CLASH]: { displayName: 'Caption Clash', icon: '💬', color: '#06b6d4' },
    [AIFeatureCategory.DAILY_CHALLENGE]: { displayName: 'Daily Challenge', icon: '📅', color: '#84cc16' },
    [AIFeatureCategory.CONTENT_MODERATION]: { displayName: 'Content Moderation', icon: '🛡️', color: '#64748b' },
    [AIFeatureCategory.NARRATIVE]: { displayName: 'Narrative Feedback', icon: '📖', color: '#f97316' },
    [AIFeatureCategory.PRESENTATION]: { displayName: 'Presentation AI', icon: '📊', color: '#a855f7' },
    [AIFeatureCategory.VISION]: { displayName: 'Vision Analysis', icon: '👁️', color: '#14b8a6' },
    [AIFeatureCategory.POSTY_IMAGE_GEN]: { displayName: 'Posty Images', icon: '💌', color: '#f43f5e' },
};

export enum AIModelUsed {
    GPT_4O_MINI = 'gpt-4o-mini',
    GPT_4O = 'gpt-4o',
    DALLE_2 = 'dall-e-2',
    DALLE_3 = 'dall-e-3',
    TTS_1 = 'tts-1',
    TTS_1_HD = 'tts-1-hd',
    MODERATION = 'text-moderation-latest',
    UNKNOWN = 'unknown'
}

/**
 * Single AI API call event
 * Stored in: aiUsage/events/items/{eventId}
 */
export interface AIUsageEvent {
    id: string;
    appleID: string;
    profileID: string;
    category: AIFeatureCategory;
    feature: string;
    model: AIModelUsed;
    origin: string;
    tokenCost: number;
    timestamp: Timestamp;
    responseTimeMs?: number;
    succeeded: boolean;
    errorMessage?: string;
    wasFlagged: boolean;
    deviceType: 'iPhone' | 'iPad';
    appVersion: string;
    metadata?: Record<string, string>;
}

/**
 * Daily aggregated AI usage analytics
 * Stored in: aiUsage/analytics/daily/{date}
 */
export interface AIUsageDailyAnalytics {
    date: string;
    totalCalls: number;
    uniqueUserIDs: string[];
    totalTokens: number;
    callsByCategory: Record<string, number>;
    tokensByCategory: Record<string, number>;
    callsByModel: Record<string, number>;
    failedCalls: number;
    flaggedCalls: number;
    totalResponseTimeMs: number;
    callsWithResponseTime: number;
    callsByDevice: Record<string, number>;
}

/**
 * Aggregated AI usage analytics across a date range (computed client-side)
 */
export interface AIUsageAggregatedAnalytics {
    startDate: string;
    endDate: string;
    totalCalls: number;
    uniqueUsers: number;
    totalTokens: number;
    avgCallsPerDay: number;
    avgResponseTimeMs: number;
    callsByCategory: Record<string, number>;
    tokensByCategory: Record<string, number>;
    callsByModel: Record<string, number>;
    failureRate: number;
    flagRate: number;
    dailyStats: AIUsageDailyAnalytics[];
}

/** Chart data point for category/model pie/bar charts */
export interface ChartData {
    label: string;
    value: number;
    color: string;
    icon?: string;
}

/** Trend data point for line/sparkline charts */
export interface TrendData {
    date: string;
    value: number;
}

/** Cost breakdown entry per category */
export interface CostData {
    category: string;
    displayName: string;
    totalTokens: number;
    totalCalls: number;
    color: string;
}

/**
 * Combined dashboard showing both AI usage + Omore interactions
 */
export interface CombinedAIDashboard {
    aiUsage: {
        todayCalls: number;
        todayTokens: number;
        todayUniqueUsers: number;
        weekCalls: number;
        weekTokens: number;
        topCategory: string;
        failureRate: number;
        callsTrend: Array<{ date: string; count: number }>;
        tokensTrend: Array<{ date: string; count: number }>;
        categoryBreakdown: Array<{ category: string; calls: number; tokens: number; color: string }>;
    };

    omoreInteractions: {
        todaySessions: number;
        todayMessages: number;
        pendingModeration: number;
        usersWithFlags: number;
        suggestionAdoptionRate: number;
        topHelperType: string;
        sessionsTrend: Array<{ date: string; count: number }>;
    };
}
